import {
    useEffect,
    createContext,
    useContext,
    useState,
    PropsWithChildren
} from "react";

import { PreloadedQuery, usePreloadedQuery } from "react-relay";
import {
    PricingPageRelayQuery,
    shapePricingContentfulResponse
} from "src/contentful";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";

import { pricingPageRelayQuery } from "@/__generated_contentful__/pricingPageRelayQuery.graphql";
import ContentNotFoundErrorBoundary from "@/content/utils/content-not-found-error-boundary";
import { useLocale } from "@/libraries/web-framework";
import {
    getPricingContent,
    PricingContent,
    PricingPlanComparison,
    PricingPlanComparisonCta
} from "@/utils/pricing-queries/get-pricing-content.ts";

import { DataCenterSectionProps } from "./data-center";

export type PricingContentContextValue = PricingContent & {
    hasError?: boolean;
};

const PricingContentContext = createContext<
    PricingContentContextValue | undefined
>(undefined);

export const usePricingContentContext = () => {
    return useContext(PricingContentContext);
};

export interface PricingContentContextProviderProps extends PropsWithChildren {
    productId: string;
    pricingTitleInternal?: string;
    pricingContent?: PricingContent;
    queryRef?: PreloadedQuery<pricingPageRelayQuery>;
}

const stripPTags = (htmlString: string): string => {
    return htmlString.replace(/<\/?p>/g, "");
};

export const PricingContentContextProvider = (
    props: PricingContentContextProviderProps
) => {
    return (
        <ContentNotFoundErrorBoundary>
            <PricingContentContextProviderInternal {...props} />
        </ContentNotFoundErrorBoundary>
    );
};

const PricingContentContextProviderInternal = (
    props: PricingContentContextProviderProps
) => {
    const {
        productId,
        pricingContent: initialPricingContent,
        pricingTitleInternal
    } = props;
    const locale = useLocale();

    const [pricingContent, setPricingContent] = useState<
        PricingContent | undefined
    >(initialPricingContent);
    const [loading, setLoading] = useState(true);

    const { createAnalyticsEvent } = useAnalyticsEvents();

    useEffect(() => {
        async function fetchPricingContent() {
            try {
                const fetchedContent = await getPricingContent(productId, {
                    locale,
                    pricingTitleInternal
                });
                setPricingContent(fetchedContent);
            } catch (error) {
                createAnalyticsEvent({
                    eventType: "track",
                    action: "failed",
                    actionSubject: "getPricingContent",
                    attributes: {
                        productId,
                        locale,
                        pricingTitleInternal,
                        // @ts-ignore
                        error: error.message
                    },
                    source: window.location.pathname
                }).fire();

                setPricingContent({
                    hasError: true
                } as PricingContentContextValue);
            } finally {
                setLoading(false);
            }
        }
        if (!initialPricingContent) {
            void fetchPricingContent();
        } else {
            setLoading(false);
        }
    }, [initialPricingContent, productId, locale]);

    const cleanedValue: PricingContent | undefined = pricingContent
        ? {
              ...pricingContent,
              headline: pricingContent?.headline
                  ? stripPTags(pricingContent.headline)
                  : ""
          }
        : undefined;

    if (loading) {
        return null;
    }

    return (
        <PricingContentContext.Provider value={cleanedValue}>
            {props.children}
        </PricingContentContext.Provider>
    );
};

export const PricingContentContextProviderRelay = (
    props: PricingContentContextProviderProps
) => {
    return (
        <ContentNotFoundErrorBoundary>
            <PricingContentContextProviderInternalRelay {...props} />
        </ContentNotFoundErrorBoundary>
    );
};

const PricingContentContextProviderInternalRelay = (
    props: PricingContentContextProviderProps
) => {
    let { pricingContent } = props;
    const [loading, setLoading] = useState(true);

    const data = usePreloadedQuery(
        PricingPageRelayQuery,
        props.queryRef ||
            (null as unknown as PreloadedQuery<pricingPageRelayQuery>)
    );

    if (!pricingContent) {
        const shapedResponse = shapePricingContentfulResponse(data);
        pricingContent = shapedResponse?.pricings?.edges?.[0]?.node;
    }

    useEffect(() => {
        if (!!pricingContent) {
            setLoading(false);
        }
    }, [pricingContent]);

    const cleanedValue: PricingContent | undefined = pricingContent
        ? {
              ...pricingContent,
              headline: pricingContent?.headline
                  ? stripPTags(pricingContent.headline)
                  : ""
          }
        : undefined;

    if (loading) {
        return null;
    }

    return (
        <PricingContentContext.Provider value={cleanedValue}>
            {props.children}
        </PricingContentContext.Provider>
    );
};

export function usePricingPlanComparison(
    title: string
): PricingPlanComparison | undefined {
    const context = usePricingContentContext();
    return context?.comparePlans?.find(
        (p): p is PricingPlanComparison => p.planTitleExternal === title
    );
}

export function useGetComparePlanCta() {
    const context = usePricingContentContext();
    return function getComparePlanCta(params: {
        comparePlanIdx: number;
        microCtaIdx?: number;
    }) {
        return context?.comparePlans?.[params.comparePlanIdx]?.microCta?.[
            params.microCtaIdx ?? 0
        ] as PricingPlanComparisonCta | undefined;
    };
}
export function usePricingPlanCta() {
    const context = usePricingContentContext();
    return function getPricingPlanCta(params: { planName: string }) {
        return context?.comparePlans?.find(
            (p): p is PricingPlanComparison =>
                p.planTitleExternal === params.planName
        )?.microCta?.[0];
    };
}

// eslint-disable-next-line complexity
export function useDatacenterPlans(
    btnText: string,
    linkText: string
): DataCenterSectionProps {
    const context = usePricingContentContext();
    const datacenterContent = context?.datacenterPlans?.[0];

    const btnCtaObject = datacenterContent?.microCta?.find(
        (btnCta) =>
            "ctaButtonText" in btnCta && btnText === btnCta.ctaButtonText
    );

    const linkCtaObject = datacenterContent?.microCta?.find(
        (linkCta) =>
            "ctaButtonText" in linkCta && linkText === linkCta.ctaButtonText
    );

    return {
        title: datacenterContent?.planTitleExternal ?? "",
        description: datacenterContent?.planOneLiner ?? "",
        buttonCta:
            btnCtaObject && "ctaUrl" in btnCtaObject
                ? {
                      label: btnCtaObject.ctaButtonText || undefined,
                      href: btnCtaObject.ctaUrl || undefined
                  }
                : undefined,
        linkCta:
            linkCtaObject && "ctaUrl" in linkCtaObject
                ? {
                      label: linkCtaObject.ctaButtonText || undefined,
                      href: linkCtaObject.ctaUrl || undefined
                  }
                : undefined
    };
}
